import * as api from '@apis/app-feed';
import HeartIcon from '@assets/images/app/icon-heart-outline-dark-home.png';
import NotificationIcon from '@assets/images/app/icon-notification-home.png';
import SearchIcon from '@assets/images/app/icon-search-home.png';
import AppLogoImage from '@assets/images/app/igogo_home.png';
import { AppLoader } from '@components/Loader/AppLoader';
import AppBottomModal from '@components/app/common/AppBottomModal';
import CategoryCell from '@components/app/main-feed/CategoryCell';
import ProductGeneralCell from '@components/app/main-feed/ProductGeneralCell';
import ProductGridCell from '@components/app/main-feed/ProductGridCell';
import ProductListCell from '@components/app/main-feed/ProductListCell';
import CommerceCarouselCell from '@components/app/main-feed/CommerceCarouselCell';
import FeedFooterCell from '@components/app/main-feed/FeedFooterCell';
import PopularKeywordsCell from '@components/app/main-feed/PopularKeywordsCell';
import SearchCell from '@components/app/main-feed/SearchCell';
import ShortcutCell from '@components/app/main-feed/ShortcutCell';
import ThemeCell from '@components/app/main-feed/ThemeCell';
import TutorOnCell from '@components/app/main-feed/TutorOnCell';
import VerticalBannerCell from '@components/app/main-feed/VerticalBannerCell';
import { Carousel } from '@components/common/Carousel';
import { HEADER_HEIGHT } from '@consts/ui';
import { DeepLinkActions, useDeeplink } from '@hooks/deeplink';
import { useAppMarketingModalStore } from '@stores/appMarketingModalStore';
import { useScrollPositionStore } from '@stores/scrollPositionStore';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';
import * as AppLayout from './AppLayout';
import './MainFeedPage.scss';

import { useAppNavigation } from '@hooks/appNavigation';
import { useAuthentication } from '@hooks/authentication';
import { FeedData, FeedDataItem } from '@models/feed';
import { useAuthStore } from '@stores/authStore';
import { useLayoutSizeStore } from '@stores/layoutSizeStore';
import { useOkCancelDialog } from '@stores/okCancelDialogStore';
import { shuffleArray } from '@utils/dataTransform';
import { cdnImageUrl } from '@utils/image';
import { setHideHomeBanner, showHomeBanner } from '@utils/string';
import { APP_BASE_PATH } from '@variables';
import { useLocation } from 'react-router-dom';
import PurchasedProductsCell from '@components/app/main-feed/PurchasedProductsCell';

const ITEM_LARGE_GAP = '20px';
const ITEM_MEDIUM_GAP = '12px';

export const MainFeedPage: React.FC = () => {
  const authStore = useAuthStore();
  const userPhone = authStore.user?.profile?.phone;
  const okCancelDialog = useOkCancelDialog();
  const { layoutSize } = useLayoutSizeStore();
  const { checkLogin } = useAuthentication();
  const virtuoso = useRef<any>();
  const scrollStore = useScrollPositionStore();
  const [marketingPagination, setMarketingPagination] = useState<number>(1);
  const [marketingBanner, setMarketingBanner] = useState<FeedData<FeedDataItem>>();
  const { showMarketingModal, setShowMarketingModal } = useAppMarketingModalStore();
  const { linkTo } = useDeeplink();
  const appNavigation = useAppNavigation();
  const location = useLocation();
  const shuffleRef = useRef<{ [key: string]: any }>({});

  useEffect(() => {
    document.title = '아이고고 | 홈';

    return () => {
      document.title = '아이고고 - 아이를 위한 모든 경험';
    };
  }, []);

  useEffect(() => {
    if (authStore.user && !userPhone) {
      okCancelDialog.open({
        title: '연락처를 등록해 주세요!',
        content: '수업일부터 이벤트 소식까지\n쉽고 빠르게 알려드릴게요.',
        type: 'ok_cancel',
        confirmText: '등록하기',
        onConfirm: () => {
          appNavigation.to(`${APP_BASE_PATH}my-page/user-profile`);
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPhone]);

  const feedQuery = useQuery({
    queryKey: [`feed-main`],
    queryFn: () => api.getMainFeed(),
  });

  const feedData = feedQuery.data?.data.data || [];

  const getBannersQuery = useQuery({
    queryKey: [`app-banners`],
    queryFn: () => api.getBanners(),
  });
  const bannersFeedData = getBannersQuery.data?.data;

  useEffect(() => {
    (bannersFeedData?.data || []).forEach((data) => {
      if (data.type === 'home-popup') {
        let banners = data.data || [];
        if (data?.shuffle) {
          banners = shuffleArray(banners);
        }
        setMarketingBanner({ ...data, data: banners });
      }
    });
  }, [bannersFeedData]);

  return (
    <AppLayout.Layout>
      <AppLayout.Header
        leftButton={
          <div>
            <div
              style={{
                height: HEADER_HEIGHT,
                backgroundColor: '#ffffff',
                justifyContent: 'center',
                alignItems: 'center',
                marginLeft: 15,
                display: 'flex',
              }}>
              <img
                style={{
                  height: '26px',
                  width: '50px',
                }}
                src={AppLogoImage}
                alt=""
              />
            </div>
          </div>
        }
        rightButton={
          <div>
            <div style={{ display: 'flex' }}>
              <button
                className="link-button"
                style={{ marginRight: '20px' }}
                onClick={() => {
                  linkTo({ action: DeepLinkActions.SEARCH_KEYWORD }, { source: 'main-feed' });
                }}>
                <div
                  style={{
                    display: 'flex',
                    width: '24px',
                    height: HEADER_HEIGHT,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <img style={{ height: '24px', width: '24px' }} src={SearchIcon} alt="" />
                </div>
              </button>
              <button
                className="link-button"
                style={{ marginRight: '20px' }}
                onClick={async () => {
                  if (await checkLogin(true)) {
                    linkTo({ action: DeepLinkActions.GO_TO_FAVORITES, payload: { tab: 'product' } });
                  }
                }}>
                <div
                  style={{
                    display: 'flex',
                    width: '24px',
                    height: HEADER_HEIGHT,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <img style={{ height: '24px', width: '24px' }} src={HeartIcon} alt="" />
                </div>
              </button>
              <button
                className="link-button"
                style={{ marginRight: '16px' }}
                onClick={async () => {
                  if (await checkLogin(true)) {
                    appNavigation.to(`${APP_BASE_PATH}alarm`);
                  }
                }}>
                <div
                  style={{
                    display: 'flex',
                    width: '24px',
                    height: HEADER_HEIGHT,
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}>
                  <img style={{ height: '24px', width: '24px' }} src={NotificationIcon} alt="" />
                </div>
              </button>
            </div>
          </div>
        }></AppLayout.Header>
      <AppLayout.Contents>
        {feedQuery.isLoading && <AppLoader />}
        {feedQuery.isSuccess && (
          <Virtuoso
            initialTopMostItemIndex={scrollStore.positions['main-feed'] || 0}
            ref={virtuoso}
            rangeChanged={(range) => {
              scrollStore.updateScrollPostion('main-feed', range.startIndex);
            }}
            style={{ height: '100%', overflowX: 'hidden' }}
            totalCount={feedData.length + 1}
            itemContent={(index) => {
              if (index === feedData.length) {
                return <FeedFooterCell />;
              }
              const item = feedData[index];
              switch (item.type) {
                case 'curation-vertical': {
                  let contents = [...(item.data || [])];
                  if (item.shuffle) {
                    contents = shuffleRef.current[`${index}`] || shuffleArray(contents);
                    if (!shuffleRef.current[`${index}`]) {
                      shuffleRef.current[`${index}`] = contents;
                    }
                  }

                  return (
                    <div style={{ padding: `${ITEM_MEDIUM_GAP} 0` }}>
                      <VerticalBannerCell data={contents} index={index}></VerticalBannerCell>
                    </div>
                  );
                }
                case 'curation-search':
                  return (
                    <div style={{ padding: `${ITEM_MEDIUM_GAP} 0` }}>
                      <SearchCell title={item.title} index={index}></SearchCell>
                    </div>
                  );
                case 'curation-shortcut':
                  return (
                    <div style={{ padding: `${ITEM_MEDIUM_GAP} 0 ${ITEM_LARGE_GAP}` }}>
                      <ShortcutCell data={item.data} index={index}></ShortcutCell>
                    </div>
                  );
                case 'curation-commerce': {
                  let contents = [...(item.data || [])];
                  if (item.shuffle) {
                    contents = shuffleRef.current[`${index}`] || shuffleArray(contents);
                    if (!shuffleRef.current[`${index}`]) {
                      shuffleRef.current[`${index}`] = contents;
                    }
                  }

                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <CommerceCarouselCell
                        data={contents}
                        title={item.title}
                        size={item.size}
                        index={index}></CommerceCarouselCell>
                    </div>
                  );
                }
                case 'curation-keywords':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <PopularKeywordsCell
                        title={item.title}
                        style={{ width: '100%', padding: '0 16px' }}
                        data={item.data}
                        onClick={(value) => {
                          linkTo(
                            { action: DeepLinkActions.SEARCH_KEYWORD, payload: { keyword: value } },
                            { source: 'main-feed-curation-keywords' },
                          );
                        }}
                        index={index}></PopularKeywordsCell>
                    </div>
                  );
                case 'curation-class-grid':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <ProductGridCell
                        collectionId={item.collectionId || item._id}
                        data={item.data}
                        title={item.title}
                        index={index}></ProductGridCell>
                    </div>
                  );
                case 'curation-class-general':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <ProductGeneralCell
                        collectionId={item.collectionId || item._id}
                        data={item.data}
                        title={item.title}
                        index={index}></ProductGeneralCell>
                    </div>
                  );
                case 'curation-tutor-on':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <TutorOnCell data={item.data} index={index}></TutorOnCell>
                    </div>
                  );
                case 'curation-category':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <CategoryCell data={item.data} index={index}></CategoryCell>
                    </div>
                  );
                case 'curation-class-list':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <ProductListCell
                        data={item.data}
                        title={item.title}
                        description={item.description}
                        index={index}></ProductListCell>
                    </div>
                  );
                case 'curation-purchased-list':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <PurchasedProductsCell
                        data={item.data}
                        title={item.title}
                        description={item.description}
                        index={index}></PurchasedProductsCell>
                    </div>
                  );
                case 'curation-class-theme':
                  return (
                    <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                      <ThemeCell title={item.title} data={item.data} index={index}></ThemeCell>
                    </div>
                  );
              }
              return (
                <div style={{ padding: `${ITEM_LARGE_GAP} 0` }}>
                  <div>{item.type}</div>
                </div>
              );
            }}
          />
        )}
      </AppLayout.Contents>
      <AppBottomModal
        open={showHomeBanner() && marketingBanner?.active && showMarketingModal}
        height={Math.floor((layoutSize.width / 1.5 + 60) / 4) * 4}
        hideHeader
        hideLeftNaviButton
        headerStyle={{
          borderRadius: '24px 24px 0 0',
        }}
        footer={
          <div
            style={{
              height: '60px',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: '#9b9da0',
              fontWeight: 'bold',
              fontSize: '14px',
              padding: '0 20px',
            }}>
            <div
              onClick={() => {
                setHideHomeBanner();
                setShowMarketingModal(false);
              }}
              style={{ cursor: 'pointer' }}>
              오늘은 그만보기
            </div>
            <div
              onClick={() => {
                setShowMarketingModal(false);
              }}
              style={{ cursor: 'pointer', color: '#242424' }}>
              닫기
            </div>
          </div>
        }
        onRightNaviButtonClick={() => {
          setShowMarketingModal(false);
        }}>
        <div
          style={{
            display: 'flex',
            flex: 1,
            width: '100%',
            height: 'auto',
            borderTopRightRadius: '24px',
            borderTopLeftRadius: '24px',
            position: 'relative',
          }}>
          <Carousel
            autoplay={marketingBanner?.autoplay}
            options={{
              dragFree: false,
              loop: true,
              align: 'start',
              containScroll: 'trimSnaps',
              startIndex: location.state?.marketingBannerIndex || 0,
            }}
            autoplayOptions={{
              delay: (marketingBanner?.delay || 0) * 1000 || 4000,
            }}
            onChangeSlide={(currentSlide: number) => {
              setMarketingPagination(currentSlide + 1);
            }}>
            {(marketingBanner?.data || []).map((item: any, index: number) => {
              return (
                <div
                  key={`main-feed-marketing-${index}`}
                  onClick={() => {
                    appNavigation.update({ path: location.pathname, state: { marketingBannerIndex: index } });
                    linkTo(item, { source: 'main-feed-marketing-banner', index: index });
                  }}
                  className={'embla__slide'}
                  style={{
                    flex: '0 0 100%',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}>
                  {
                    <img
                      style={{ width: '100%', aspectRatio: 1.5, objectFit: 'cover' }}
                      src={cdnImageUrl(item.imageUrl)}
                      alt=""
                    />
                  }
                </div>
              );
            })}
          </Carousel>
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              padding: '6px 12px',
              backgroundColor: 'rgba(0,0,0,0.5)',
              borderRadius: '100px',
              fontSize: '11px',
            }}>
            <span style={{ color: 'white' }}>{marketingPagination}</span>&nbsp;
            <span style={{ color: 'rgba(255, 255, 255, 0.6)' }}>/ {(marketingBanner?.data || []).length}</span>
          </div>
        </div>
      </AppBottomModal>
      <AppLayout.Footer
        onScollToTop={() => {
          if (virtuoso.current) {
            virtuoso.current.scrollTo({ top: 0 });
          }
        }}></AppLayout.Footer>
    </AppLayout.Layout>
  );
};

export default MainFeedPage;
